import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./TrueCare.css";
import trcrSection from "../../assets/trcr-section.png";
import trcrSection1 from "../../assets/trcr-section1.png";
import trcrSection2 from "../../assets/trcr-section2.png";
import trcrSection3 from "../../assets/trcr-section3.png";
import trcrSection4Img from "../../assets/trcr-section4.png";
import ecoBg from "../../assets/eco-bg.png";
import truerenewableslogo from "../../assets/truerenewables_logo.svg";
import trcrSection7Img1 from "../../assets/trcr-section7-img1.png";
import trcrSection7Img2 from "../../assets/trcr-section7-img2.png";
import trcrSection8Img from "../../assets/trcr-section8.png";
import trcrSection9Img from "../../assets/trcr-section9.png";
import trcrSection10Img from "../../assets/trcr-section10.png";
import trcrSection11Img1 from "../../assets/trcr-section11-img1.png";
import trcrSection11Img2 from "../../assets/trcr-section11-img2.png";
import trcrAdv1 from "../../assets/tco-adv1.png";
import trcrAdv2 from "../../assets/tco-adv2.png";
import trcrAdv3 from "../../assets/tco-adv3.png";
import trcrAdv4 from "../../assets/tco-adv4.png";
import careBenefits1 from "../../assets/carebenefits1-1.png";
import careBenefits2 from "../../assets/carebenefits1-2.png";
import careBenefits3 from "../../assets/trucareoverall-3.png";
import careBenefits4 from "../../assets/carebenefits1-4.png";
import careBenefits5 from "../../assets/carebenefits1-5.png";
import careBenefits6 from "../../assets/carebenefits1-6.png";
import trackBenefits1 from "../../assets/trackbenefits-1.png";
import trackBenefits2 from "../../assets/trackbenefits-2.png";
import trackBenefits3 from "../../assets/trackbenefits-3.png";
import trackBenefits4 from "../../assets/truetrack-overall-4.png";
import trackBenefits5 from "../../assets/trackbenefits-5.png";

/* ── shared reveal hook ── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.65) {
        el.classList.add("is-visible");
        window.removeEventListener("scroll", check);
      }
    };
    const timer = setTimeout(() => {
      window.addEventListener("scroll", check, { passive: true });
      check();
    }, 400);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", check);
    };
  }, []);
  return ref;
};


/* ================================================
   SECTION 1 — HERO
   ================================================ */
const TrueCareHero = () => {
  const navigate = useNavigate();
  return (
  <section
    className="trcr-hero-section"
    style={{ "--eco-bg": `url(${ecoBg})` }}
  >
    <div className="trcr-hero-container">
      <div className="trcr-hero-left trcr-hero-left-animate">
        <h1 className="trcr-hero-title">
          Maximum Uptime.{"\n"}Predictive Maintenance.{"\n"}Intelligent Plant
          Health.
        </h1>
        <p className="trcr-hero-desc">
          TrueCare™ is an advanced O&amp;M intelligence platform designed to
          monitor your plant 24/7, predict failures before they happen, analyse
          performance in real time, and ensure your system operates at its
          maximum potential for 25+ years.
        </p>
        <button className="trcr-hero-btn" onClick={() => navigate("/contact")}>Book A Free Call</button>
      </div>
      <div className="trcr-hero-right">
        <picture>
          {/* Mobile image */}
          <source media="(max-width: 767px)" srcSet={trcrSection1} />

          {/* Desktop + Tablet image */}
          <img
            src={trcrSection}
            alt="TrueTrack Hero"
            className="tt-hero-phone"
          />
        </picture>
      </div>
    </div>
  </section>
);
}
/* ================================================
   SECTION 2 — WHAT IS TRUECARE
   ================================================ */
const trcrCheckItems = [
  "SCADA data",
  "IoT sensors",
  "Panel-level or string-level monitoring",
  "ML-based analytics",
  "Performance benchmarks",
  "Fault diagnostics",
  "Automated ticketing systems",
];

const TrueCareAbout = () => {
  const rightRef = useReveal();
  return (
    <section className="trcr-about-section">
      <div className="trcr-about-left">
        <img
          src={trcrSection2}
          alt="TrueCare Dashboard"
          className="trcr-about-img"
          loading="lazy"
        />
      </div>
      <div className="trcr-about-right reveal-block" ref={rightRef}>
        <p className="trcr-about-heading">
          TrueCare™ is True Renewables' AI-powered O&amp;M platform that
          transforms traditional maintenance into a fully digital, automated and
          intelligence-led system.
        </p>
        <div className="trcr-about-integrates">
          <p className="trcr-about-integrates-label">It integrates:</p>
          <ul className="trcr-about-list">
            {trcrCheckItems.map((item) => (
              <li key={item} className="trcr-about-list-item">
                <svg
                  className="trcr-check-icon"
                  width="16"
                  height="13"
                  viewBox="0 0 16 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 6L6 11L15 1"
                    stroke="#C04820"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="trcr-about-footer">
          TrueCare™ ensures your plant is not just running — it is running at
          peak performance every single day.
        </p>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 3 — WHY TRUECARE EXISTS
   ================================================ */
const trcrLeftCards = [
  {
    num: "01",
    text: "You don't know what's happening unless someone tells you.",
  },
  { num: "02", text: "Faults are discovered only after generation drops." },
  { num: "03", text: "Maintenance is done only when something fails." },
];
const trcrRightCards = [
  { num: "04", text: "MIS reports come late — if at all." },
  { num: "05", text: "Downtime remains unmeasured." },
  { num: "06", text: "No one takes responsibility." },
];

const TrueCareWhyExists = () => {
  const headerRef = useReveal();
  return (
    <section className="trcr-why">
      <div className="trcr-why-header reveal-block" ref={headerRef}>
        <p className="trcr-why-tag">
          <span className="trcr-why-dot" />
          WHY CHOOSE US
        </p>
        <h2 className="trcr-why-title">Why TrueCare™ Exists</h2>
        <p className="trcr-why-subtitle">
          Traditional solar O&amp;M is broken:
        </p>
      </div>

      <div className="trcr-why-body">
        <div className="trcr-why-col">
          {trcrLeftCards.map((c) => (
            <div className="trcr-why-card" key={c.num}>
              <p className="trcr-why-num">{c.num}</p>
              <p className="trcr-why-text">{c.text}</p>
            </div>
          ))}
        </div>
        <div className="trcr-why-img-wrap">
          <img
            src={trcrSection3}
            alt="Why TrueCare Exists"
            className="trcr-why-img"
            loading="lazy"
          />
        </div>
        <div className="trcr-why-col">
          {trcrRightCards.map((c) => (
            <div className="trcr-why-card" key={c.num}>
              <p className="trcr-why-num">{c.num}</p>
              <p className="trcr-why-text">{c.text}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="trcr-why-footer">
        TrueCare™ fixes this with data transparency, intelligent automation, and
        predictive insights.
      </p>
    </section>
  );
};

/* ================================================
   SECTION 4 — KEY CAPABILITIES
   ================================================ */
const trcrCapItems = [
  {
    title: "24/7 Live Monitoring Dashboard",
    desc: "Your plant's performance is visible at all times:",
    checks: [
      "Solar generation",
      "Inverter output",
      "PR & CUF",
      "Module health",
      "Weather impact",
      "Live load (for C&I rooftop)",
      "Alerts and warnings",
    ],
    img: careBenefits1,
  },
  {
    title: "AI-Based Fault Detection & Prediction",
    desc: "Prediction → Prevention → Uptime:",
    checks: [
      "String mismatch detection",
      "Hotspot pattern recognition",
      "Sudden drops in DC/AC ratio",
      "Temperature spikes & fault repetition patterns",
      "Degradation forecast",
    ],
    img: careBenefits2,
  },
  {
    title: "Automated Ticketing & SLA Management",
    desc: "When a fault occurs:",
    checks: [
      "TrueCare™ detects it",
      "Creates a ticket",
      "Assigns it to the O&M engineer",
      "Tracks resolution time",
      "Ensures SLA compliance",
    ],
    img: careBenefits3,
  },
  {
    title: "PR & CUF Analytics",
    desc: "Daily, weekly, and monthly insights:",
    checks: [
      "Performance Ratio",
      "Capacity Utilisation Factor",
      "Loss analysis & seasonal patterns",
      "Comparative performance (plant vs expected)",
    ],
    img: careBenefits4,
  },
  {
    title: "Preventive Maintenance Planner",
    desc: "A smart calendar ensures:",
    checks: [
      "Cleaning schedules & tightening checks",
      "Thermography sessions & Array inspection",
      "Inverter servicing",
      "Seasonal adjustments",
    ],
    img: careBenefits5,
  },
  {
    title: "Smart Alerts & Notifications",
    desc: "Automated notifications for:",
    checks: [
      "Low generation & zero generation",
      "Inverter tripping & weather risks",
      "Soiling losses & communication failure",
      "Unauthorized access (for utility-scale sites)",
    ],
    img: careBenefits6,
  },
];

const TrueCareCapabilities = () => {
  const headerRef = useReveal();
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fillPct, setFillPct] = useState(0);

  const activeIndexRef = useRef(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const DURATION = 2500;

  const startTimer = (newIndex) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    activeIndexRef.current = newIndex;
    setActiveIndex(newIndex);
    setFillPct(0);
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const pct = Math.min((elapsed / DURATION) * 100, 100);
      setFillPct(pct);
      if (pct < 100) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        const next = (activeIndexRef.current + 1) % trcrCapItems.length;
        startTimer(next);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTimer(0);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const barStyle = {
    background: `linear-gradient(to bottom, #f05a28 0%, #f05a28 ${fillPct}%, #e4e4e4 ${fillPct}%, #e4e4e4 100%)`,
  };

  return (
    <section className="trcr-cap" ref={sectionRef}>
      <div className="trcr-cap-header reveal-block" ref={headerRef}>
        <p className="trcr-cap-tag">
          <span className="trcr-cap-dot" />
          OVERALL BENEFITS
        </p>
        <h2 className="trcr-cap-title">Key Capabilities of TrueCare™</h2>
      </div>

      <div className="trcr-cap-body">
        <div className="trcr-cap-list">

          {/* First item */}
          <div>
            <div
              className={`trcr-cap-item ${
                activeIndex === 0 ? "trcr-cap-item--active" : ""
              }`}
              onClick={() => startTimer(0)}
            >
              {activeIndex === 0 && <span className="trcr-cap-bar" style={barStyle} />}
              <div className="trcr-cap-text">
                <p className="trcr-cap-item-title">{trcrCapItems[0].title}</p>
                {activeIndex === 0 && (
                  <>
                    <p className="trcr-cap-item-desc">{trcrCapItems[0].desc}</p>
                    <ul className="trcr-cap-checklist">
                      {trcrCapItems[0].checks.map((check, j) => (
                        <li key={j} className="trcr-cap-check-item">
                          {trcrCapItems[0].isNumbered ? (
                            <span className="trcr-cap-check-num">{j + 1}.</span>
                          ) : (
                            <svg
                              width="16" height="12" viewBox="0 0 16 12"
                              fill="none" xmlns="http://www.w3.org/2000/svg"
                              className="trcr-cap-check-icon"
                            >
                              <path
                                d="M1 5.5L5.5 10L15 1"
                                stroke="#C04820" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {check}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
            {/* Mobile-only inline image — appears below the active item */}
            <div className="trcr-cap-mobile-img-wrap">
              <div className="trcr-cap-mobile-img-bg" />
              <img
                src={trcrCapItems[0].img}
                alt={trcrCapItems[0].title}
                className="trcr-cap-mobile-img"
                loading="lazy"
              />
            </div>
            <hr className="trcr-cap-divider" />
          </div>

          {/* Remaining items */}
          {trcrCapItems.slice(1).map((item, i) => {
            const idx = i + 1;
            return (
              <div key={idx}>
                <div
                  className={`trcr-cap-item ${
                    activeIndex === idx ? "trcr-cap-item--active" : ""
                  }`}
                  onClick={() => startTimer(idx)}
                >
                  {activeIndex === idx && <span className="trcr-cap-bar" style={barStyle} />}
                  <div className="trcr-cap-text">
                    <p className="trcr-cap-item-title">{item.title}</p>
                    {activeIndex === idx && (
                      <>
                        <p className="trcr-cap-item-desc">{item.desc}</p>
                        <ul className="trcr-cap-checklist">
                          {item.checks.map((check, j) => (
                            <li key={j} className="trcr-cap-check-item">
                              {item.isNumbered ? (
                                <span className="trcr-cap-check-num">{j + 1}.</span>
                              ) : (
                                <svg
                                  width="16" height="12" viewBox="0 0 16 12"
                                  fill="none" xmlns="http://www.w3.org/2000/svg"
                                  className="trcr-cap-check-icon"
                                >
                                  <path
                                    d="M1 5.5L5.5 10L15 1"
                                    stroke="#C04820" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                              {check}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
                {/* Mobile-only inline image — appears below the active item */}
                <div className="trcr-cap-mobile-img-wrap">
                  <div className="trcr-cap-mobile-img-bg" />
                  <img
                    src={item.img}
                    alt={item.title}
                    className="trcr-cap-mobile-img"
                    loading="lazy"
                  />
                </div>
                <hr className="trcr-cap-divider" />
              </div>
            );
          })}
        </div>

        {/* Desktop-only: image on the right */}
        <div className="trcr-cap-img-wrap trcr-cap-img-wrap--desktop">
          <div className="trcr-cap-img-bg" />
          <img
            src={trcrCapItems[activeIndex].img}
            alt="Key Capabilities"
            className="trcr-cap-img"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 5 — SINGLE WINDOW OF TRUTH
   ================================================ */
const trcrLeftRow1 = ["Corporate Clients", "Government Clients"];
const trcrLeftRow2 = ["Group Captive Consumers", "Residential Clients"];
const trcrRightRow1 = ["Industrial Clients", "DISCOMS"];
const trcrRightRow2 = ["Investors", "Asset Managers"];

const TrueCareSingleWindow = () => (
  <section className="trcr-sw-section">
    <div className="trcr-sw-orbit">
      {/* Left pills — scroll rightward toward center */}
      <div className="trcr-sw-side trcr-sw-side--left">
        <div className="trcr-sw-row">
          <div className="trcr-pills-track trcr-pills-track--right">
            {[...trcrLeftRow1, ...trcrLeftRow1].map((label, i) => (
              <span className="trcr-pill" key={`lr1-${i}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
        <div className="trcr-sw-row">
          <div className="trcr-pills-track trcr-pills-track--right trcr-pills-track--delay">
            {[...trcrLeftRow2, ...trcrLeftRow2].map((label, i) => (
              <span className="trcr-pill" key={`lr2-${i}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Center logo card */}
      <div className="trcr-sw-center">
        <div className="trcr-sw-logo-card">
          <img
            src={truerenewableslogo}
            alt="True Renewables"
            className="trcr-sw-logo"
            loading="lazy"
          />
        </div>
      </div>

      {/* Right pills — scroll leftward toward center */}
      <div className="trcr-sw-side trcr-sw-side--right">
        <div className="trcr-sw-row">
          <div className="trcr-pills-track trcr-pills-track--left">
            {[...trcrRightRow1, ...trcrRightRow1].map((label, i) => (
              <span className="trcr-pill" key={`rr1-${i}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
        <div className="trcr-sw-row">
          <div className="trcr-pills-track trcr-pills-track--left trcr-pills-track--delay">
            {[...trcrRightRow2, ...trcrRightRow2].map((label, i) => (
              <span className="trcr-pill" key={`rr2-${i}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>

    <p className="trcr-sw-footer">
      You see everything.
      <br />
      Nothing is <strong>hidden</strong>. Nothing is <strong>delayed</strong>.
      Nothing is <strong>lost</strong>.
    </p>
  </section>
);

/* ================================================
   SECTION 6 — TRUECARE WORKFLOW
   ================================================ */
const trcrWorkflowSteps = [
  { num: 1, title: "Data Acquisition", desc: "IoT + SCADA + inverter feeds." },
  {
    num: 2,
    title: "AI Analytics Engine",
    desc: "Fault detection, prediction, benchmarking.",
  },
  {
    num: 3,
    title: "O&M Actions",
    desc: "Ticket creation, assignment, resolution.",
  },
  {
    num: 4,
    title: "Reporting Engine",
    desc: "Performance metrics, health reports, ESG-ready data.",
  },
  {
    num: 5,
    title: "Integration with TrueCom™",
    desc: "Automated billing, carbon credits, ROI analytics.",
  },
];

const TrueCareWorkflow = () => {
  const sectionRef = useRef(null);
  const stepRefs = useRef([]);
  const lineRef = useRef(null);
  const leftRef = useReveal();
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    const onScroll = () => {
      const section = sectionRef.current;
      const line = lineRef.current;
      if (!section || !line) return;

      const rect = section.getBoundingClientRect();
      const totalHeight = section.offsetHeight;
      const scrolled = -rect.top + window.innerHeight * 0.3;
      const progress = Math.min(Math.max(scrolled / totalHeight, 0), 1);

      const lineTrackEl = line.parentElement;
      const lineTrackHeight = lineTrackEl.offsetHeight;
      const lineFilledPx = progress * 1.4 * lineTrackHeight;

      line.style.transform = `scaleY(${Math.min(progress * 1.4, 1)})`;

      let newActive = 0;
      stepRefs.current.forEach((stepEl, i) => {
        if (!stepEl) return;
        const circleEl = stepEl.querySelector(".trcr-wf-circle");
        if (!circleEl) return;
        const lineTrackTop = lineTrackEl.getBoundingClientRect().top;
        const circleCenterY =
          circleEl.getBoundingClientRect().top + circleEl.offsetHeight / 2;
        const circlePxFromLineTop = circleCenterY - lineTrackTop;
        if (lineFilledPx >= circlePxFromLineTop) newActive = i + 1;
      });

      setActiveStep(newActive);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="trcr-wf-section" ref={sectionRef}>
      <div className="trcr-wf-container">
        {/* Left — sticky label block, no subtitle */}
        <div className="trcr-wf-left reveal-block" ref={leftRef}>
          <p className="trcr-wf-tag">
            <span className="trcr-wf-dot" />
            HOW IT WORKS
          </p>
          <h2 className="trcr-wf-title">TrueCare™ Workflow</h2>
        </div>

        {/* Right: timeline */}
        <div className="trcr-wf-timeline">
          <div className="trcr-wf-line-track">
            <div className="trcr-wf-line-bg" />
            <div className="trcr-wf-line-fill" ref={lineRef} />
          </div>

          {trcrWorkflowSteps.map((step, i) => {
            const isActive = i + 1 <= activeStep;
            return (
              <div
                className={`trcr-wf-step${isActive ? " trcr-wf-step--active" : ""}`}
                key={step.num}
                ref={(el) => (stepRefs.current[i] = el)}
              >
                <div className="trcr-wf-circle">
                  <span className="trcr-wf-num">{step.num}</span>
                </div>
                <div className="trcr-wf-step-content">
                  <p className="trcr-wf-step-title">{step.title}</p>
                  <p className="trcr-wf-step-desc">{step.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 7 — INTEGRATION WITH TRUETRACK & TRUECOM
   ================================================ */

const trcrIntegrationCards = [
  {
    id: 1,
    title: "TrueTrack™ — EPC Intelligence Platform",
    desc: "Your plant's baseline data is transferred seamlessly into TrueCare™ at commissioning.",
    imgMain: trcrSection7Img1,
  },
  {
    id: 2,
    title: "TrueCom™ — Commercial, Billing & Compliance Automation",
    desc: "Performance data — billing, settlements, REC/I-REC issuance.",
    imgMain: trcrSection7Img2,
  },
];

const TrueCareIntegration = () => {
  const cardRefs = useRef([]);
  const rafRef = useRef(null);
  const headerRef = useReveal();
  const footerRef = useReveal();

  useEffect(() => {
    const sectionEl = cardRefs.current[0]?.closest(".cir-section");

    const update = () => {
      if (!sectionEl) return;
      const sectionRect = sectionEl.getBoundingClientRect();
      const scrolled = Math.max(0, -sectionRect.top);
      const maxScroll = Math.max(
        1,
        sectionEl.offsetHeight - window.innerHeight,
      );
      const t = Math.min(1, scrolled / maxScroll);
      const globalScale = 1 - t * 0.12;

      cardRefs.current.forEach((card) => {
        if (!card) return;
        card.style.transform = `scale(${globalScale})`;
      });

      rafRef.current = null;
    };

    const onScroll = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section className="cir-section">
      <div className="cir-container">
        {/* Header — no subtitle */}
        <div className="cir-header reveal-block" ref={headerRef}>
          <p className="cir-tag">
            <span className="cir-dot" />
            TRUECARE 360
          </p>
          <h2 className="cir-title">
            Integration with TrueTrack™ &amp; TrueCom™
          </h2>
        </div>

        {/* Cards */}
        <div className="cir-cards">
          {trcrIntegrationCards.map((card, index) => (
            <div
              key={card.id}
              className="cir-card"
              style={{ top: `${80 + index * 20}px`, zIndex: index + 1 }}
              ref={(el) => (cardRefs.current[index] = el)}
            >
              <div className="cir-left">
                <span className="cir-card-dot" />
                <div className="cir-left-inner">
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                  <button className="cir-btn">Learn More</button>
                </div>
                <span className="cir-card-dot" />
              </div>
              <div className="cir-right">
                <img
                  src={card.imgMain}
                  alt={card.title}
                  className="cir-img-main"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <p className="cir-footer reveal-block" ref={footerRef}>
          Your plant remains digitally connected for its entire life.
        </p>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 8 — WHO BENEFITS
   ================================================ */
const trcrBenefitItems = [
  {
    title: "Industrial & Corporate Clients",
    desc: "Complete clarity over execution of captive, group captive and rooftop plants.",
    img: trackBenefits1,
  },
  {
    title: "Government & DISCOM",
    desc: "Reliable O&M for KUSUM feeders and rooftop plants.",
    img: trackBenefits2,
  },
  {
    title: "Residential Clients",
    desc: "Performance transparency for all shareholders.",
    img: trackBenefits3,
  },
  {
    title: "Investors",
    desc: "Fast, transparent rooftop installations.",
    img: trackBenefits4,
  },
  {
    title: "Internal Teams",
    desc: "Compliance-ready performance insights and climate reporting.",
    img: trackBenefits5,
  },
];

const TrueCareBenefits = () => {
  const headerRef = useReveal();
  const [activeIndex, setActiveIndex] = useState(0);
  const outerRef = useRef(null);
  const activeIndexRef = useRef(0);

  /* ── Pin the panel while scrolling through the outer (tall) wrapper,
     driving activeIndex from scroll progress. ── */
  useEffect(() => {
    let raf;

    const update = () => {
      const el = outerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const totalScroll = el.offsetHeight - window.innerHeight;
        const scrolled = -rect.top;
        const p =
          totalScroll > 0
            ? Math.max(0, Math.min(scrolled / totalScroll, 1))
            : 0;

        const idx = Math.min(
          trcrBenefitItems.length - 1,
          Math.floor(p * trcrBenefitItems.length)
        );

        if (idx !== activeIndexRef.current) {
          activeIndexRef.current = idx;
          setActiveIndex(idx);
        }
      }

      raf = requestAnimationFrame(update);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    const timer = setTimeout(() => update(), 100);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="trcr-ben-outer"
      ref={outerRef}
      style={{ height: `${trcrBenefitItems.length * 110}vh` }}
    >
      <section className="trcr-ben-sticky">
        <div className="trcr-ben">
          <div className="trcr-ben-header reveal-block" ref={headerRef}>
            <p className="trcr-ben-tag">
              <span className="trcr-ben-dot" />
              OVERALL BENEFITS
            </p>
            <h2 className="trcr-ben-title">
              Why TrueCare™ Makes True Renewables Different
            </h2>
          </div>

          <div className="trcr-ben-body">
            <div className="trcr-ben-list">
              {trcrBenefitItems.map((item, i) => (
                <div key={i}>
                  <div
                    className={`trcr-ben-item ${
                      i === activeIndex ? "trcr-ben-item--active" : ""
                    }`}
                    onClick={() => setActiveIndex(i)}
                  >
                    {/* Orange bar — only for active */}
                    {i === activeIndex && <span className="trcr-ben-bar" />}

                    <div className="trcr-ben-text">
                      <p className="trcr-ben-item-title">{item.title}</p>

                      {/* Desc — only for active, animates in */}
                      {i === activeIndex && (
                        <p className="trcr-ben-desc">{item.desc}</p>
                      )}
                    </div>
                  </div>

                  {/* Mobile-only inline image — appears below the active item */}
                  <div className="trcr-ben-mobile-img-wrap">
                    <div className="trcr-ben-mobile-img-bg" />
                    <img
                      src={item.img}
                      alt={item.title}
                      className="trcr-ben-mobile-img"
                      loading="lazy"
                    />
                  </div>

                  <hr className="trcr-ben-divider" />
                </div>
              ))}
            </div>

            {/* Desktop image */}
            <div className="trcr-ben-img-wrap trcr-ben-img-wrap--desktop">
              <div className="trcr-ben-img-bg" />
              <img
                src={trcrBenefitItems[activeIndex].img}
                alt="Why TrueCare Makes True Renewables Different"
                className="trcr-ben-img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ================================================
   SECTION 9 — WHY TRUECARE MAKES US DIFFERENT
   ================================================ */

const trcrAdvItems = [
  "4/7 intelligence instead of reactive service",
  "Automated workflows instead of manual tracking",
  "AI-based predictions instead of late fault detection",
  "Documented transparency instead of assumptions",
  "SLA-driven operations instead of verbal follow-ups",
  "End-to-end lifecycle visibility instead of fragmented data",
];

const TrueCareAdvantages = () => {
  const headerRef = useReveal();

  return (
    <section className="trcr-adv-section">
      {/* Header — reuses section 8 header classes */}
      <div className="trcr-ben-header reveal-block" ref={headerRef}>
        <p className="trcr-ben-tag">
          <span className="trcr-ben-dot" />
          OVERALL BENEFITS
        </p>
        <h2 className="trcr-adv-title">
          Why TrueCare™ Makes True Renewables Different
        </h2>
      </div>

      {/* Card row */}
      <div className="trcr-adv-inner">
        {/* Left — image */}
        <div className="trcr-adv-img-wrap">
          <img
            src={trcrSection9Img}
            alt="Why TrueCare Makes True Renewables Different"
            className="trcr-adv-img"
            loading="lazy"
          />
        </div>

        {/* Right — card */}
        <div className="trcr-adv-card">
          <div className="trcr-adv-card-top">
            <p className="trcr-adv-heading">
              Where most EPCs stop at installation, we go 10 layers deeper:
            </p>
            <ul className="trcr-adv-list">
              {trcrAdvItems.map((item, i) => (
                <li key={i} className="trcr-adv-list-item">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="trcr-adv-check-icon"
                  >
                    <path
                      d="M4 12.5L9.5 18L20 7"
                      stroke="#C04820"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="trcr-adv-footer-text">
            TrueCare™ makes solar predictable, healthy and financially
            optimized.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 10 — THE FUTURE OF PLANT PERFORMANCE
   ================================================ */

const trcrFutureItems = [
  "Digital twin simulations",
  "Autonomous drone inspections",
  "Machine vision thermography",
  "Weather-driven performance forecasting",
  "Solar + battery optimization",
  "Grid-interactive smart controls",
  "Complete closed-loop O&M automation",
];

const TrueCareFuture = () => {
  const leftRef = useReveal();
  return (
    <section className="trcr-future">
      <div className="trcr-future-inner">
        <div className="trcr-future-left reveal-block" ref={leftRef}>
          <div className="trcr-future-header">
            <p className="trcr-future-tag">
              <span className="trcr-future-dot" />
              NEXT-GENERATION EXECUTION
            </p>
            <h2 className="trcr-future-title">
              The Future Of Plant{"\n"}Performance
            </h2>
          </div>

          <p className="trcr-future-subtitle">TrueCare™ is evolving toward:</p>

          <ul className="trcr-future-list">
            {trcrFutureItems.map((item, i) => (
              <li key={i} className="trcr-future-list-item">
                <svg
                  className="trcr-future-check"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12.5L9.5 18L20 7"
                    stroke="#C04820"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {item}
              </li>
            ))}
          </ul>

          <p className="trcr-future-footer trcr-future-footer--desktop">
            Our goal is simple:
            <br />
            Zero downtime. Zero uncertainty. Maximum lifetime performance.
          </p>
        </div>

        <div className="trcr-future-img-wrap">
          <div className="trcr-future-img-bg" />
          <img
            src={trcrSection10Img}
            alt="The Future Of Plant Performance"
            className="trcr-future-img"
            loading="lazy"
          />
        </div>

        <p className="trcr-future-footer trcr-future-footer--mobile">
          Our goal is simple:
          <br />
          Zero downtime. Zero uncertainty. Maximum lifetime performance.
        </p>
      </div>
    </section>
  );
};
/* ================================================
   SECTION 11 — DUAL-DASHBOARD ARCHITECTURE
   ================================================ */

const trcrDashTabs = [
  {
    id: "engineering",
    label: "Engineering Dashboard",
    img: trcrSection11Img1,
    title: "Engineering Dashboard",
    subtitle:
      "Built for EPC, O&M and SCADA teams who need deep technical visibility.",
    checks: [
      "SCADA real-time interface",
      "String-level & module-level analytics",
      "Power curve diagnostics",
      "Inverter-level performance",
      "Grid stability & synchronization parameters",
      "Alarm logs & fault signatures",
      "Temperature correlations (module & inverter)",
    ],
    tagline:
      "It gives your engineering team all the data needed to diagnose issues, optimize performance and predict failures — just like a utility-grade control room.",
  },
  {
    id: "owner",
    label: "Owner Dashboard",
    img: trcrSection11Img2,
    title: "Owner Dashboard",
    subtitle:
      "Designed for plant owners, investors, corporates, government officials and decision-makers.",
    checks: [
      "Daily / Weekly / Monthly generation",
      "Plant uptime (%)",
      "Savings achieved",
      "Scheduled maintenance reminders",
      "Ticket status (open / in progress / closed)",
      "Cleaning schedules",
      "PR & CUF presented in simplified graphs",
    ],
    tagline:
      "This dashboard removes technical noise and presents exactly what the owner needs for decision-making — nothing more, nothing less.",
  },
];

const TrueCareDualDashboard = () => {
  const [activeTab, setActiveTab] = useState("engineering");
  const sectionRef = useRef(null);
  const leftRef = useReveal();
  const active = trcrDashTabs.find((t) => t.id === activeTab);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const onScroll = () => {
      
      const rect = section.getBoundingClientRect();
      const totalScroll = section.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const progress = scrolled / totalScroll;
      setActiveTab(progress >= 0.5 ? "owner" : "engineering");
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="trcr-dual-scroll-wrapper" ref={sectionRef}>
      <section className="trcr-dual">
        <div className="trcr-dual-inner trcr-dual-desktop">
          {/* ── LEFT ── */}
          <div className="trcr-dual-left reveal-block" ref={leftRef}>
            <div className="trcr-dual-left-header">
              <p className="trcr-dual-tag">
                <span className="trcr-dual-dot" />
                CLARITY AND CONTROL
              </p>
              <h2 className="trcr-dual-title">Dual-Dashboard Architecture</h2>
              <p className="trcr-dual-subtitle">
                Just like TrueCom™, TrueCare™ serves two distinct user types:
              </p>
            </div>

            <div className="trcr-dual-tabs">
              {trcrDashTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`trcr-dual-tab${activeTab === tab.id ? " trcr-dual-tab--active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {activeTab === tab.id && (
                    <span className="trcr-dual-tab-bar" />
                  )}
                  <span className="trcr-dual-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>

            <p className="trcr-dual-tagline">{active.tagline}</p>
          </div>

          {/* ── RIGHT ── */}
          <div className="trcr-dual-right">
            <img
              src={active.img}
              alt={active.title}
              className="trcr-dual-right-img"
              loading="lazy"
            />
            <p className="trcr-dual-right-title">{active.title}</p>
            <p className="trcr-dual-right-subtitle">{active.subtitle}</p>
            <ul className="trcr-dual-checklist">
              {active.checks.map((c, i) => (
                <li key={i} className="trcr-dual-check-item">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="trcr-dual-check-icon"
                  >
                    <path
                      d="M5 12L10 17L19 7"
                      stroke="#C04820"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── MOBILE accordion layout ── */}
        <div className="trcr-dual-mobile">
          <div className="trcr-dual-mobile-header">
            <p className="trcr-dual-tag">
              <span className="trcr-dual-dot" />
              CLARITY AND CONTROL
            </p>
            <h2 className="trcr-dual-title">Dual-Dashboard Architecture</h2>
            <p className="trcr-dual-subtitle">
              Just like TrueCom™, TrueCare™ serves two distinct user types:
            </p>
          </div>

          {[...trcrDashTabs]
            .sort((a) => (a.id === activeTab ? -1 : 1))
            .map((tab, idx) => (
              <div
                key={tab.id}
                className={`trcr-dual-acc-item${activeTab === tab.id ? " trcr-dual-acc-item--active" : ""}${idx === 0 ? " trcr-dual-acc-item--first" : ""}`}
              >
                <button
                  className="trcr-dual-acc-trigger"
                  onClick={() => setActiveTab(tab.id)}
                >
                  {activeTab === tab.id && (
                    <span className="trcr-dual-tab-bar" />
                  )}
                  <span className="trcr-dual-tab-label">{tab.label}</span>
                </button>

                {activeTab === tab.id && (
                  <div className="trcr-dual-acc-content">
                    <p className="trcr-dual-right-subtitle">{tab.subtitle}</p>
                    <ul className="trcr-dual-checklist">
                      {tab.checks.map((c, i) => (
                        <li key={i} className="trcr-dual-check-item">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="trcr-dual-check-icon"
                          >
                            <path
                              d="M5 12L10 17L19 7"
                              stroke="#C04820"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {c}
                        </li>
                      ))}
                    </ul>
                    <img
                      src={tab.img}
                      alt={tab.title}
                      className="trcr-dual-acc-img"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      </section>
    </div>
  );
};

/* ================================================
   SECTION 12 — WHY DUAL DASHBOARDS MATTER
   ================================================ */

const trcrAdvCards = [
  {
    icon: trcrAdv1,
    text: (
      <>
        Engineers and owners think differently and they need{" "}
        <strong>DEPTH</strong>, not simplification
      </>
    ),
  },
  {
    icon: trcrAdv2,
    text: (
      <>
        Owners need <strong>CLARITY</strong>, not complexity
      </>
    ),
  },
  {
    icon: trcrAdv3,
    text: (
      <>
        It avoids <strong>CONFUSION</strong>, <strong>MISINTERPRETATION</strong>{" "}
        and <strong>MISCOMMUNICATION</strong>
      </>
    ),
  },
  {
    icon: trcrAdv4,
    text: (
      <>
        It elevates the plant's transparency to{" "}
        <strong>WORLD-CLASS STANDARDS</strong>
      </>
    ),
  },
];

const TrueCareDashAdvantages = () => {
  const headerRef = useReveal();
  return (
    <section className="trcr-dadv">
      <div className="trcr-dadv-header reveal-block" ref={headerRef}>
        <p className="trcr-dadv-tag">
          <span className="trcr-dadv-dot" />
          DUAL DASHBOARD ADVANTAGES
        </p>
        <h2 className="trcr-dadv-title">Why Dual Dashboards Matter</h2>
      </div>

      <div className="trcr-dadv-cards">
        {trcrAdvCards.map((c, i) => (
          <div className="trcr-dadv-card" key={i}>
            <div className="trcr-dadv-icon">
              <img src={c.icon} alt="" width="32" height="32" loading="lazy" />
            </div>
            <p className="trcr-dadv-card-text">{c.text}</p>
          </div>
        ))}
      </div>

      <p className="trcr-dadv-footer">
        This dual-interface framework positions TrueCare™ as a professional,
        enterprise-grade O&amp;M powerhouse
      </p>
    </section>
  );
};
/* ================================================
   TRUECARE PAGE
   ================================================ */
const TrueCare = () => (
  <>
    <TrueCareHero />
    <TrueCareAbout />
    <TrueCareWhyExists />
    <TrueCareCapabilities />
    <TrueCareSingleWindow />
    <TrueCareWorkflow />
    <TrueCareIntegration />
    <TrueCareBenefits />
    <TrueCareAdvantages />
    <TrueCareFuture />
    <TrueCareDualDashboard />
    <TrueCareDashAdvantages />
  </>
);

export default TrueCare;
